// src/pages/Dashboard/GraphicalDashboard.js
// Graphical view of the admin dashboard. Uses exactly the same data object as the
// statistical view (GET /api/dashboard/summary), so both views always agree and both
// update automatically when requirements / applications change.
// Pure SVG — no chart library needed.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// ── Colours (fixed order, never cycled) ──
const C = {
  teal: '#008986',
  amber: '#c2620a',
  purple: '#6b5bd2',
  gray: '#9aa8a6',
  track: '#e8efee',
  grid: '#e4ecea',
  ink: '#1a2422',
  muted: '#5c6c6a',
  total: '#3a4644',
};

// Application statuses grouped into 4 readable buckets for stacked charts
// ── Shared visibility rules (used by both views) ──
// Applied / Training / Joined are the stages the app uses today, so they are always shown.
// Every other status (shortlisted, interview, offered, rejected, withdrawn, or any status
// added later) appears automatically as soon as at least one application has it.
export const CORE_STATUSES = ['applied', 'training', 'joined'];
export const visiblePipeline = (pipeline = []) =>
  pipeline.filter((p) => CORE_STATUSES.includes(p.key) || p.count > 0);
// Vacancy sections are shown only once at least one requirement has a vacancy count.
export const hasVacancyData = (data) =>
  (data?.requirements || []).some((r) => r.vacancies != null) ||
  (data?.locations || []).some((l) => l.vacancies > 0);

const BUCKETS = [
  { key: 'applied', label: 'Applied', color: C.purple, statuses: ['applied'], core: true },
  { key: 'in_process', label: 'Shortlisted / interview / offered', color: '#2f6fbf', statuses: ['shortlisted', 'interview', 'offered'] },
  { key: 'training', label: 'Training', color: C.amber, statuses: ['training'], core: true },
  { key: 'joined', label: 'Joined', color: C.teal, statuses: ['joined'], core: true },
  { key: 'dropped', label: 'Rejected / withdrawn', color: C.gray, statuses: ['rejected', 'withdrawn'] },
];
const KNOWN_BUCKET_STATUSES = BUCKETS.flatMap((b) => b.statuses);
// Any application status added in future that is not in a bucket above lands here,
// so totals always add up.
const OTHER_BUCKET = { key: 'other', label: 'Other statuses', color: '#d55181', statuses: [] };

// Colours for values that have no fixed colour (new statuses etc.) — fixed order, then grey.
const FALLBACK_COLORS = ['#6b5bd2', '#d55181', '#2f6fbf'];

const LABELS = { whatsapp: 'WhatsApp', sms: 'SMS' };

// ── Application sources: works for any source value, including ones added later ──
// website / whatsapp keep fixed colours; new sources take the next free slot
// (in alphabetical order, so a source never changes colour when filters change);
// anything beyond the available slots is grouped as "Other".
const SOURCE_FIXED = { website: '#008986', whatsapp: '#c2620a' };
const SOURCE_EXTRA = ['#6250d6', '#d55181'];
const SOURCE_OTHER = '#9aa8a6';

/**
 * keys       = sources present in this chart
 * masterKeys = every source in the database (filter_options.sources). Colour slots are
 *              assigned from the master list, so a source has the same colour in every chart.
 */
export function groupSources(keys, masterKeys = []) {
  const present = new Set(keys.filter(Boolean));
  const master = [...new Set([...masterKeys, ...present])];
  const extras = master.filter((k) => !SOURCE_FIXED[k]).sort();
  const slotOf = (k) => (SOURCE_FIXED[k] ? SOURCE_FIXED[k] : SOURCE_EXTRA[extras.indexOf(k)]);

  const series = [];
  const rest = [];
  [...Object.keys(SOURCE_FIXED), ...extras].forEach((k) => {
    if (!present.has(k)) return;
    const color = slotOf(k);
    if (color) series.push({ key: k, label: humanizeKey(k), color, members: [k] });
    else rest.push(k);
  });
  if (rest.length) {
    series.push({ key: '__other', label: `Other (${rest.map(humanizeKey).join(', ')})`, color: SOURCE_OTHER, members: rest });
  }
  return series;
}

/** [{key, count}] → grouped [{key, label, color, count}] */
export function groupSourceCounts(items, masterKeys = []) {
  const counts = Object.fromEntries(items.map((i) => [i.key, i.count]));
  return groupSources(items.map((i) => i.key), masterKeys).map((g) => ({
    ...g,
    count: g.members.reduce((s, m) => s + (counts[m] || 0), 0),
  }));
}

function humanizeKey(key) {
  return (
    LABELS[key] ||
    String(key || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}
const humanize = humanizeKey;
const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);

const formatPeriod = (period, granularity) => {
  if (granularity === 'day') {
    return new Date(`${period}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  }
  const [y, m] = period.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
};

/** Width of a container, kept in sync on resize — lets SVGs render at real pixel size. */
function useWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const el = ref.current;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

// ───────────────────────────── shared tooltip ─────────────────────────────

const TooltipContext = React.createContext({ show: () => {}, hide: () => {} });

const TooltipLayer = ({ tip }) => {
  if (!tip) return null;
  const left = Math.min(tip.x + 14, window.innerWidth - 240);
  return (
    <div className="jg-tooltip" style={{ left, top: tip.y + 14 }}>
      {tip.title && <div className="jg-tooltip-title">{tip.title}</div>}
      {(tip.rows || []).map((r) => (
        <div className="jg-tooltip-row" key={r.label}>
          {r.color && <span className="jg-swatch" style={{ background: r.color }} />}
          <span>{r.label}</span>
          <strong>{r.value}</strong>
        </div>
      ))}
    </div>
  );
};

const useTip = () => React.useContext(TooltipContext);

// ───────────────────────────── chart pieces ─────────────────────────────

const Panel = ({ title, subtitle, children, className = '', action }) => (
  <section className={`jd-card jg-panel ${className}`}>
    <header className="jd-card-head">
      <div>
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {action}
    </header>
    <div className="jd-card-body">{children}</div>
  </section>
);

const NoData = ({ text = 'No data for the selected filters' }) => (
  <div className="jd-empty"><i className="fas fa-chart-area" /> {text}</div>
);

const Legend = ({ items }) => (
  <ul className="jd-legend jg-legend">
    {items.map((i) => (
      <li key={i.key || i.label}>
        <span className="jd-dot" style={{ background: i.color }} />
        {i.label}
        {i.count != null && <strong>{fmt(i.count)}</strong>}
      </li>
    ))}
  </ul>
);

/** Donut with 2px surface gaps between slices and a total in the centre. */
const Donut = ({ items, centerLabel = 'Total', size = 168 }) => {
  const { show, hide } = useTip();
  const total = items.reduce((s, i) => s + i.count, 0);
  if (!total) return <NoData />;
  const r = size / 2 - 4;
  const inner = r * 0.62;
  const cx = size / 2;
  let angle = -Math.PI / 2;
  const gap = items.filter((i) => i.count).length > 1 ? 0.025 : 0;

  const arc = (a0, a1) => {
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const p = (rad, a) => [cx + rad * Math.cos(a), cx + rad * Math.sin(a)];
    const [x0, y0] = p(r, a0);
    const [x1, y1] = p(r, a1);
    const [x2, y2] = p(inner, a1);
    const [x3, y3] = p(inner, a0);
    return `M${x0},${y0} A${r},${r} 0 ${large} 1 ${x1},${y1} L${x2},${y2} A${inner},${inner} 0 ${large} 0 ${x3},${y3} Z`;
  };

  return (
    <div className="jg-donut">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={centerLabel}>
        {items.filter((i) => i.count).map((i) => {
          const sweep = (i.count / total) * Math.PI * 2;
          const a0 = angle + gap / 2;
          const a1 = angle + Math.max(sweep - gap / 2, gap / 2 + 0.001);
          angle += sweep;
          const full = i.count === total;
          return full ? (
            <g key={i.key}>
              <circle cx={cx} cy={cx} r={(r + inner) / 2} fill="none" stroke={i.color} strokeWidth={r - inner}
                onMouseMove={(e) => show(e, { title: i.label, rows: [{ label: 'Count', value: `${fmt(i.count)} (100%)` }] })}
                onMouseLeave={hide} />
            </g>
          ) : (
            <path key={i.key} d={arc(a0, a1)} fill={i.color} className="jg-hover"
              onMouseMove={(e) => show(e, { title: i.label, rows: [{ label: 'Count', value: `${fmt(i.count)} (${pct(i.count, total)}%)` }] })}
              onMouseLeave={hide} />
          );
        })}
        <text x={cx} y={cx - 4} textAnchor="middle" className="jg-donut-value">{fmt(total)}</text>
        <text x={cx} y={cx + 16} textAnchor="middle" className="jg-donut-label">{centerLabel}</text>
      </svg>
      <Legend items={items} />
    </div>
  );
};

/** Semicircle gauge for a single ratio. */
const Gauge = ({ value, max, label, sub, color = C.teal }) => {
  const ratio = max ? Math.min(value / max, 1) : 0;
  const w = 180;
  const r = 72;
  const cx = w / 2;
  const cy = 86;
  const end = Math.PI + ratio * Math.PI;
  const x = cx + r * Math.cos(end);
  const y = cy + r * Math.sin(end);
  return (
    <div className="jg-gauge">
      <svg width={w} height={100} viewBox={`0 0 ${w} 100`} role="img" aria-label={label}>
        <path d={`M${cx - r},${cy} A${r},${r} 0 0 1 ${cx + r},${cy}`} fill="none" stroke={C.track} strokeWidth="14" strokeLinecap="round" />
        {ratio > 0 && (
          <path d={`M${cx - r},${cy} A${r},${r} 0 0 1 ${x},${y}`} fill="none" stroke={color} strokeWidth="14" strokeLinecap="round" />
        )}
        <text x={cx} y={cy - 8} textAnchor="middle" className="jg-gauge-value">{max ? `${pct(value, max)}%` : '—'}</text>
      </svg>
      <div className="jg-gauge-label">{label}</div>
      <div className="jg-gauge-sub">{sub}</div>
    </div>
  );
};

/** Hiring funnel: centred bars that narrow by stage, with stage-to-stage conversion. */
const Funnel = ({ pipeline }) => {
  const { show, hide } = useTip();
  const map = Object.fromEntries(pipeline.map((p) => [p.key, p.count]));
  const FULL_ORDER = ['applied', 'shortlisted', 'interview', 'offered', 'training', 'joined'];
  // core stages always; optional stages only once used
  const order = FULL_ORDER.filter((k) => CORE_STATUSES.includes(k) || (map[k] || 0) > 0);
  // "reached" = candidates at this stage or any later stage
  const stages = order.map((key, idx) => ({
    key,
    current: map[key] || 0,
    reached: order.slice(idx).reduce((s, k) => s + (map[k] || 0), 0),
  }));
  const dropped = (map.rejected || 0) + (map.withdrawn || 0);
  // Statuses added in future (not part of the funnel order) are listed separately, never hidden
  const others = pipeline.filter(
    (p) => p.count > 0 && !order.includes(p.key) && !['rejected', 'withdrawn'].includes(p.key)
  );
  const top = stages[0].reached;
  if (!top && !dropped && !others.length) return <NoData />;

  return (
    <div className="jg-funnel">
      {stages.map((s, idx) => {
        const width = top ? Math.max((s.reached / top) * 100, 2) : 2;
        const prev = idx ? stages[idx - 1].reached : null;
        return (
          <div className="jg-funnel-row" key={s.key}
            onMouseMove={(e) => show(e, {
              title: humanize(s.key),
              rows: [
                { label: 'Reached this stage', value: fmt(s.reached) },
                { label: 'Currently here', value: fmt(s.current) },
                ...(prev != null ? [{ label: 'From previous stage', value: `${pct(s.reached, prev)}%` }] : []),
              ],
            })}
            onMouseLeave={hide}>
            <span className="jg-funnel-label">{humanize(s.key)}</span>
            <span className="jg-funnel-track">
              <span className="jg-funnel-bar" style={{ width: `${width}%`, opacity: 1 - idx * 0.09 }} />
            </span>
            <span className="jg-funnel-value">
              {fmt(s.reached)}
              {prev != null && <em>{pct(s.reached, prev)}%</em>}
            </span>
          </div>
        );
      })}
      {dropped > 0 && (
        <div className="jg-funnel-drop">
          <i className="fas fa-user-times" /> Rejected / withdrawn: <strong>{fmt(dropped)}</strong>
          <span>({fmt(map.rejected)} rejected, {fmt(map.withdrawn)} withdrawn)</span>
        </div>
      )}
      {others.length > 0 && (
        <div className="jg-funnel-drop">
          <i className="fas fa-pause-circle" /> Other statuses:{' '}
          {others.map((o, i) => (
            <span key={o.key}>{i ? ', ' : ''}{humanize(o.key)} <strong>{fmt(o.count)}</strong></span>
          ))}
        </div>
      )}
    </div>
  );
};

/** Line chart with crosshair hover: total + one line per application source. */
const TrendLines = ({ trend, masterSources }) => {
  const { show, hide } = useTip();
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const points = trend?.points || [];
  const series = useMemo(() => groupSources(trend?.sources || [], masterSources), [trend, masterSources]);
  const valueOf = (p, g) => g.members.reduce((s, m) => s + ((p.by_source || {})[m] || 0), 0);
  const H = 250;
  const pad = { l: 36, r: 12, t: 14, b: 30 };

  const geo = useMemo(() => {
    if (!width || !points.length) return null;
    const max = Math.max(...points.map((p) => p.total), 1);
    const niceMax = Math.ceil(max / Math.pow(10, Math.floor(Math.log10(max)))) * Math.pow(10, Math.floor(Math.log10(max)));
    const iw = width - pad.l - pad.r;
    const ih = H - pad.t - pad.b;
    const x = (i) => pad.l + (points.length === 1 ? iw / 2 : (i / (points.length - 1)) * iw);
    const y = (v) => pad.t + ih - (v / niceMax) * ih;
    const line = (get) => points.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(get(p))}`).join(' ');
    const area = `${line((p) => p.total)} L${x(points.length - 1)},${y(0)} L${x(0)},${y(0)} Z`;
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => Math.round(niceMax * t));
    return { x, y, line, area, ticks: [...new Set(ticks)], iw };
  }, [width, points]); // eslint-disable-line react-hooks/exhaustive-deps

  const onMove = useCallback(
    (e) => {
      if (!geo) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      let best = 0;
      points.forEach((p, i) => {
        if (Math.abs(geo.x(i) - mx) < Math.abs(geo.x(best) - mx)) best = i;
      });
      const p = points[best];
      setHover(best);
      show(e, {
        title: formatPeriod(p.period, trend.granularity),
        rows: [
          { label: 'Total', value: fmt(p.total), color: C.total },
          ...series.map((g) => ({ label: g.label, value: fmt(valueOf(p, g)), color: g.color })),
        ],
      });
    },
    [geo, points, show, trend, series] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const labelEvery = Math.max(1, Math.ceil(points.length / Math.max(1, Math.floor((width || 600) / 70))));

  return (
    <div ref={ref} className="jg-chart">
      {!points.length ? <NoData /> : geo && (
        <>
          <Legend items={[{ key: '__total', label: 'Total', color: C.total }, ...series]} />
          <svg width={width} height={H} role="img" aria-label="Applications over time"
            onMouseMove={onMove} onMouseLeave={() => { setHover(null); hide(); }}>
            {geo.ticks.map((t) => (
              <g key={t}>
                <line x1={pad.l} x2={width - pad.r} y1={geo.y(t)} y2={geo.y(t)} stroke={C.grid} />
                <text x={pad.l - 8} y={geo.y(t) + 4} textAnchor="end" className="jg-axis">{t}</text>
              </g>
            ))}
            <path d={geo.area} fill={C.total} opacity="0.06" />
            {series.map((g) => (
              <path key={g.key} d={geo.line((p) => valueOf(p, g))} fill="none" stroke={g.color} strokeWidth="2" />
            ))}
            <path d={geo.line((p) => p.total)} fill="none" stroke={C.total} strokeWidth="2" strokeDasharray="5 4" />
            {hover != null && (
              <line x1={geo.x(hover)} x2={geo.x(hover)} y1={pad.t} y2={H - pad.b} stroke={C.muted} strokeDasharray="3 3" />
            )}
            {points.map((p, i) => (
              <g key={p.period}>
                {(points.length <= 24 || hover === i) && (
                  <circle cx={geo.x(i)} cy={geo.y(p.total)} r={hover === i ? 5 : 3.5} fill={C.total} stroke="#fff" strokeWidth="2" />
                )}
                {i % labelEvery === 0 && (
                  <text x={geo.x(i)} y={H - 8} textAnchor="middle" className="jg-axis">
                    {formatPeriod(p.period, trend.granularity)}
                  </text>
                )}
              </g>
            ))}
          </svg>
        </>
      )}
    </div>
  );
};

/** One horizontal stacked bar per requirement, split into the 4 buckets. */
const RequirementStacks = ({ requirements, onPick }) => {
  const { show, hide } = useTip();
  const rows = requirements
    .map((r) => {
      const parts = BUCKETS.map((b) => ({
        ...b,
        count: b.statuses.reduce((s, st) => s + (r.status_counts[st] || 0), 0),
      }));
      const otherStatuses = Object.keys(r.status_counts).filter((st) => !KNOWN_BUCKET_STATUSES.includes(st));
      const otherCount = otherStatuses.reduce((s, st) => s + (r.status_counts[st] || 0), 0);
      parts.push({
        ...OTHER_BUCKET,
        label: otherStatuses.length ? `Other (${otherStatuses.map(humanize).join(', ')})` : OTHER_BUCKET.label,
        count: otherCount,
      });
      return { r, parts, total: r.total_applications };
    })
    .sort((a, b) => b.total - a.total);
  const used = (key) => rows.some((x) => x.parts.find((p) => p.key === key).count > 0);
  const legend = [...BUCKETS, OTHER_BUCKET].filter((b) => b.core || used(b.key));
  const max = Math.max(...rows.map((x) => x.total), 1);
  if (!rows.length) return <NoData text="No requirements created yet" />;

  return (
    <>
      <Legend items={legend} />
      <div className="jg-stacks">
        {rows.map(({ r, parts, total }) => (
          <div className="jg-stack-row" key={r.id}
            onClick={() => onPick && onPick(r.id)}
            onMouseMove={(e) => show(e, {
              title: `${r.requirement_code} — ${r.title}`,
              rows: [
                ...parts
                  .filter((p) => p.key !== 'other' || p.count > 0)
                  .map((p) => ({ label: p.label, value: fmt(p.count), color: p.color })),
                { label: 'Total applications', value: fmt(total) },
              ],
            })}
            onMouseLeave={hide}>
            <span className="jg-stack-label">
              <strong>{r.title}</strong>
              <em>{r.requirement_code} · {humanize(r.status)}</em>
            </span>
            <span className="jg-stack-track">
              {total ? (
                <span className="jg-stack-fill" style={{ width: `${(total / max) * 100}%` }}>
                  {parts.filter((p) => p.count).map((p) => (
                    <span key={p.key} style={{ flexGrow: p.count, background: p.color }} />
                  ))}
                </span>
              ) : <span className="jg-stack-zero">No applications yet</span>}
            </span>
            <span className="jg-stack-value">{fmt(total)}</span>
          </div>
        ))}
      </div>
    </>
  );
};

/** Bullet chart: vacancies (track) vs joined (bar) per requirement that has vacancies set. */
const VacancyBullets = ({ requirements }) => {
  const { show, hide } = useTip();
  const rows = requirements.filter((r) => r.vacancies);
  if (!rows.length) return <NoData text="No vacancy counts set on requirements" />;
  const max = Math.max(...rows.map((r) => Math.max(r.vacancies, r.filled)), 1);
  return (
    <div className="jg-bullets">
      {rows.map((r) => (
        <div className="jg-bullet-row" key={r.id}
          onMouseMove={(e) => show(e, {
            title: r.title,
            rows: [
              { label: 'Vacancies', value: fmt(r.vacancies), color: C.track },
              { label: 'Joined', value: fmt(r.filled), color: C.teal },
              { label: 'Remaining', value: fmt(r.remaining_vacancy) },
            ],
          })}
          onMouseLeave={hide}>
          <span className="jg-bullet-label">{r.title}</span>
          <span className="jg-bullet-track">
            <span className="jg-bullet-cap" style={{ width: `${(r.vacancies / max) * 100}%` }} />
            <span className="jg-bullet-bar" style={{ width: `${(Math.min(r.filled, r.vacancies) / max) * 100}%` }} />
          </span>
          <span className="jg-bullet-value">{fmt(r.filled)}/{fmt(r.vacancies)}</span>
        </div>
      ))}
    </div>
  );
};

/** Grouped columns per location: vacancies / applications / joined. */
const LocationColumns = ({ locations, showVacancies }) => {
  const { show, hide } = useTip();
  const [ref, width] = useWidth();
  const rows = locations.slice(0, 12);
  const H = 240;
  const pad = { l: 34, r: 8, t: 12, b: 42 };
  const series = [
    ...(showVacancies ? [{ key: 'vacancies', label: 'Vacancies', color: C.purple }] : []),
    { key: 'applications', label: 'Applications', color: C.amber },
    { key: 'joined', label: 'Joined', color: C.teal },
  ];
  const n = series.length;
  const max = Math.max(...rows.flatMap((r) => series.map((s) => r[s.key])), 1);
  const ih = H - pad.t - pad.b;
  const groupW = width ? (width - pad.l - pad.r) / Math.max(rows.length, 1) : 0;
  const barW = Math.max(Math.min((groupW - 14) / n - 2, 26), 4);
  const y = (v) => pad.t + ih - (v / max) * ih;
  const ticks = [...new Set([0, 0.5, 1].map((t) => Math.round(max * t)))];

  return (
    <div ref={ref} className="jg-chart">
      {!rows.length ? <NoData /> : width > 0 && (
        <>
          <Legend items={series} />
          <svg width={width} height={H} role="img" aria-label="Location-wise vacancies, applications and joined">
            {ticks.map((t) => (
              <g key={t}>
                <line x1={pad.l} x2={width - pad.r} y1={y(t)} y2={y(t)} stroke={C.grid} />
                <text x={pad.l - 6} y={y(t) + 4} textAnchor="end" className="jg-axis">{t}</text>
              </g>
            ))}
            {rows.map((r, gi) => {
              const gx = pad.l + gi * groupW + (groupW - (barW * n + 2 * (n - 1))) / 2;
              return (
                <g key={r.name}
                  onMouseMove={(e) => show(e, {
                    title: r.name,
                    rows: [
                      ...series.map((s) => ({ label: s.label, value: fmt(r[s.key]), color: s.color })),
                      { label: 'Requirements', value: fmt(r.requirements) },
                    ],
                  })}
                  onMouseLeave={hide}>
                  <rect x={pad.l + gi * groupW} y={pad.t} width={groupW} height={ih} fill="transparent" />
                  {series.map((s, si) => {
                    const v = r[s.key];
                    const h = Math.max(pad.t + ih - y(v), v ? 2 : 0);
                    return (
                      <rect key={s.key} x={gx + si * (barW + 2)} y={pad.t + ih - h} width={barW} height={h}
                        rx={Math.min(4, barW / 2)} fill={s.color} />
                    );
                  })}
                  <text x={pad.l + gi * groupW + groupW / 2} y={H - 22} textAnchor="middle" className="jg-axis">
                    {r.name.length > 12 ? `${r.name.slice(0, 11)}…` : r.name}
                  </text>
                </g>
              );
            })}
          </svg>
          {locations.length > 12 && <p className="jd-muted">Showing top 12 of {locations.length} locations by applications.</p>}
        </>
      )}
    </div>
  );
};

/** Horizontal bars for departments (single hue). */
const DepartmentBars = ({ departments }) => {
  const { show, hide } = useTip();
  if (!departments.length) return <NoData />;
  const max = Math.max(...departments.map((d) => d.applications), 1);
  return (
    <div className="jg-bullets">
      {departments.map((d) => (
        <div className="jg-bullet-row" key={d.name}
          onMouseMove={(e) => show(e, {
            title: d.name,
            rows: [
              { label: 'Requirements', value: fmt(d.requirements) },
              { label: 'Open', value: fmt(d.open) },
              { label: 'Applications', value: fmt(d.applications), color: C.teal },
              { label: 'Joined', value: fmt(d.joined) },
            ],
          })}
          onMouseLeave={hide}>
          <span className="jg-bullet-label">{d.name}</span>
          <span className="jg-bullet-track">
            <span className="jg-bullet-bar" style={{ width: `${Math.max((d.applications / max) * 100, d.applications ? 2 : 0)}%` }} />
          </span>
          <span className="jg-bullet-value">{fmt(d.applications)}</span>
        </div>
      ))}
    </div>
  );
};

/** Two-part 100% bar used for payment status pairs. */
/** 100% bar with any number of parts ({key, label, count, color}). */
const PartsBar = ({ label, parts }) => {
  const { show, hide } = useTip();
  const total = parts.reduce((s, p) => s + p.count, 0);
  return (
    <div className="jg-split">
      <div className="jg-split-head"><span>{label}</span><strong>{fmt(total)}</strong></div>
      <div className="jg-split-bar"
        onMouseMove={(e) => show(e, {
          title: label,
          rows: parts.map((p) => ({ label: p.label, value: fmt(p.count), color: p.color })),
        })}
        onMouseLeave={hide}>
        {total ? (
          parts.filter((p) => p.count > 0).map((p) => (
            <span key={p.key} style={{ flexGrow: p.count, background: p.color }} />
          ))
        ) : <span className="jg-split-empty" />}
      </div>
      <Legend items={parts} />
    </div>
  );
};

const SplitBar = ({ label, a, b }) => <PartsBar label={label} parts={[a, b]} />;

// Communication statuses → colour; any status added later gets a fallback colour.
const COMM_COLORS = { sent: C.teal, delivered: '#2f6fbf', read: C.purple, pending: C.amber, failed: C.gray };
const commParts = (statusMap, allStatuses) =>
  allStatuses.map((st, i) => ({
    key: st,
    label: humanize(st),
    count: statusMap[st] || 0,
    color: COMM_COLORS[st] || FALLBACK_COLORS[i % FALLBACK_COLORS.length],
  }));

// ───────────────────────────── the view ─────────────────────────────

const GraphicalDashboard = ({ data, onNavigate, onPickRequirement, stipendMonth, onStipendMonthChange }) => {
  const [tip, setTip] = useState(null);
  const tipApi = useMemo(
    () => ({
      show: (e, content) => setTip({ x: e.clientX, y: e.clientY, ...content }),
      hide: () => setTip(null),
    }),
    []
  );
  useEffect(() => {
    const clear = () => setTip(null);
    window.addEventListener('scroll', clear, true);
    return () => window.removeEventListener('scroll', clear, true);
  }, []);

  const k = data.kpis;
  const showVacancies = hasVacancyData(data);
  const reqStatusColors = { open: C.teal, draft: C.amber, closed: C.gray };
  const toneMap = (map) => (items) => {
    // known keys keep their colour; unknown keys (added later) take fallback colours in a stable order
    const unknown = items.map((i) => i.key).filter((key) => !map[key]).sort();
    return items.map((i) => ({
      ...i,
      label: humanize(i.key),
      color: map[i.key] || FALLBACK_COLORS[unknown.indexOf(i.key)] || C.gray,
    }));
  };

  // every communication status that exists (sent, failed, pending, … or any new one)
  const commTotals = {};
  data.communications.forEach((c) =>
    Object.entries(c.statuses || {}).forEach(([st, n]) => { commTotals[st] = (commTotals[st] || 0) + n; })
  );
  const commStatuses = [
    ...['sent', 'delivered', 'read', 'pending', 'failed'].filter((st) => st in commTotals),
    ...Object.keys(commTotals).filter((st) => !['sent', 'delivered', 'read', 'pending', 'failed'].includes(st)).sort(),
  ];

  return (
    <TooltipContext.Provider value={tipApi}>
      {/* Row 1: gauges + requirement status donut */}
      <div className={`jg-grid jg-grid-hero ${showVacancies ? '' : 'jg-hero-3'}`}>
        {showVacancies && (
          <Panel title="Vacancy fill" subtitle="Joined vs total vacancies">
            <Gauge value={k.vacancies_total - k.vacancies_remaining} max={k.vacancies_total}
              label={`${fmt(k.vacancies_total - k.vacancies_remaining)} of ${fmt(k.vacancies_total)} filled`}
              sub={`${fmt(k.vacancies_remaining)} seats remaining`} />
          </Panel>
        )}
        <Panel title="Conversion" subtitle="Applications that reached joining">
          <Gauge value={k.joined} max={k.applications_total} color={C.purple}
            label={`${fmt(k.joined)} joined of ${fmt(k.applications_total)}`}
            sub={`${fmt(k.in_training)} currently in training`} />
        </Panel>
        <Panel title="Requirements" subtitle="By status">
          <Donut centerLabel="Requirements"
            items={toneMap(reqStatusColors)(data.requirement_status)} size={150} />
        </Panel>
        <Panel title="Application source" subtitle="Where candidates applied from">
          <Donut centerLabel="Applications" size={150} items={groupSourceCounts(data.sources, data.filter_options?.sources)} />
        </Panel>
      </div>

      {/* Row 2: funnel + trend */}
      <div className="jd-grid jd-grid-2">
        <Panel title="Hiring funnel" subtitle="How far candidates progress · % = conversion from previous stage">
          <Funnel pipeline={data.pipeline} />
        </Panel>
        <Panel title="Applications over time"
          subtitle={data.trend.granularity === 'day' ? 'Daily, selected range' : 'Monthly, last 12 months'}>
          <TrendLines trend={data.trend} masterSources={data.filter_options?.sources} />
        </Panel>
      </div>

      {/* Row 3: every requirement */}
      <Panel className="jg-wide" title="Applications per requirement"
        subtitle="Every requirement, split by where its candidates are · click a bar to filter the dashboard to it">
        <RequirementStacks requirements={data.requirements} onPick={onPickRequirement} />
      </Panel>

      {/* Row 4: vacancies + departments */}
      <div className={`jd-grid ${showVacancies ? 'jd-grid-2' : ''}`}>
        {showVacancies && (
          <Panel title="Vacancies filled per requirement" subtitle="Grey = vacancies · teal = joined">
            <VacancyBullets requirements={data.requirements} />
          </Panel>
        )}
        <Panel title="Applications by department" subtitle="Hover for requirements, open and joined">
          <DepartmentBars departments={data.departments} />
        </Panel>
      </div>

      {/* Row 5: locations */}
      <Panel className="jg-wide" title="Location-wise comparison"
        subtitle={showVacancies ? 'Vacancies, applications and joinings per location' : 'Applications and joinings per location'}>
        <LocationColumns locations={data.locations} showVacancies={showVacancies} />
      </Panel>

      {/* Row 6: joining, documents, tickets */}
      <div className="jd-grid jd-grid-3">
        <Panel title="Documents" subtitle="Training + joined candidates">
          <Donut centerLabel="Candidates" size={150}
            items={toneMap({ submitted: C.teal, partially_submitted: C.amber, not_submitted: C.gray })(data.documents)} />
        </Panel>
        <Panel title="Joining form" subtitle="Joined candidates">
          <Donut centerLabel="Joined" size={150}
            items={toneMap({ completed: C.teal, in_progress: C.amber, not_started: C.gray })(data.joined_forms)} />
        </Panel>
        <Panel title="Support tickets" subtitle="All tickets"
          action={<button type="button" className="jd-link" onClick={() => onNavigate('/admin/tickets')}>Open</button>}>
          <Donut centerLabel="Tickets" size={150}
            items={toneMap({ open: C.amber, in_progress: C.purple, resolved: C.teal, closed: C.gray })(data.tickets)} />
        </Panel>
      </div>

      {/* Row 7: payments + communications */}
      <div className="jd-grid jd-grid-2">
        <Panel title="Stipend / salary payments" subtitle={`₹${fmt(data.stipends.total_amount)} recorded for ${data.stipends.month}`}
          action={onStipendMonthChange && (
            <input type="month" className="jd-month" value={stipendMonth || data.stipends.month}
              onChange={(e) => onStipendMonthChange(e.target.value)} />
          )}>
          <SplitBar label="Government payment"
            a={{ key: 'r', label: 'Received', count: data.stipends.govt_received, color: C.teal }}
            b={{ key: 'n', label: 'Not received', count: data.stipends.govt_not_received, color: C.gray }} />
          <SplitBar label="Paid to candidates"
            a={{ key: 'p', label: 'Paid', count: data.stipends.candidate_paid, color: C.teal }}
            b={{ key: 'n', label: 'Not paid', count: data.stipends.candidate_not_paid, color: C.gray }} />
        </Panel>
        <Panel title="Communications" subtitle="Messages sent to candidates">
          {data.communications.length ? (
            <>
              <PartsBar label="All channels" parts={commParts(commTotals, commStatuses)} />
              {data.communications.map((c) => (
                <PartsBar key={c.channel} label={humanize(c.channel)} parts={commParts(c.statuses || {}, commStatuses)} />
              ))}
            </>
          ) : <NoData text="No messages sent yet" />}
        </Panel>
      </div>

      <TooltipLayer tip={tip} />
    </TooltipContext.Provider>
  );
};

export default GraphicalDashboard;
